import { useContext } from 'react';
import useSWR from 'swr';
import { UseSWRMutationResult } from '../../common/types/swrTypes';
import { buildQueryString, getFromApi } from '../../common/utils/apiHelper';
import { ConfigContext } from '../configuration/ConfigContext';
import { usePropertyAssociationContext } from '../propertyAssociation/PropertyAssociationContext';
import { Booking } from './models/Booking';
import { BookingDetails } from './models/BookingDetails';

type UseBookingResult = UseSWRMutationResult<BookingDetails, 'booking'>;

export const useBooking = (bookingId: string | undefined): UseBookingResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId } = usePropertyAssociationContext();

    const url = bookingId && currentPropertyAssociationId ? `${apiBaseUrl}/propertyassociations/${currentPropertyAssociationId}/bookings/${bookingId}` : null;

    const { data, error, isLoading, mutate } = useSWR<BookingDetails>(url);

    return {
        booking: data,
        isLoading,
        error,
        refetch: () => mutate(),
    };
};

type UseBookingsResult = UseSWRMutationResult<Booking[], 'bookings'>;

export const useActiveBookings = (assetId: string | undefined): UseBookingsResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId, currentArea } = usePropertyAssociationContext();

    const url = currentPropertyAssociationId
        ? `${apiBaseUrl}/propertyassociations/${currentPropertyAssociationId}/bookings/active${buildQueryString({
              areaId: currentArea?.propertyAssociationAreaId,
              assetId,
          })}`
        : null;

    const { data, error, isLoading, mutate } = useSWR<Booking[]>(url);

    return {
        bookings: data,
        isLoading,
        error,
        refetch: () => mutate(),
    };
};

export const useArchivedBookings = (assetId: string | undefined): UseBookingsResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId, currentArea } = usePropertyAssociationContext();

    const url = currentPropertyAssociationId
        ? `${apiBaseUrl}/propertyassociations/${currentPropertyAssociationId}/bookings/inactive${buildQueryString({
              areaId: currentArea?.propertyAssociationAreaId,
              assetId,
          })}`
        : null;

    const { data, error, isLoading, mutate } = useSWR<Booking[]>(url);

    return {
        bookings: data,
        isLoading,
        error,
        refetch: () => mutate(),
    };
};

export const getBooking = (apiBaseUrl: string, propertyAssociationId: string, bookingId: string, onSuccess: (data: BookingDetails) => void, onFail: () => void): void => {
    getFromApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/bookings/${bookingId}`, onSuccess, onFail);
};

export const cancelBooking = (apiBaseUrl: string, propertyAssociationId: string, bookingsId: string, applyCancellationFee: boolean, onSucces: () => void, onFail: () => void): void => {
    getFromApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/bookings/${bookingsId}/cancel?applyCancellationFee=${applyCancellationFee}`, onSucces, onFail);
};
